import { useRef, useCallback } from 'react';
import type * as CodeMirror from 'codemirror';
import { type Controlled as ReactCodeMirror } from 'react-codemirror2';

interface CodeMirrorWrapper {
	hydrated?: boolean;
}

/**
 * Custom hook for safe CodeMirror usage in React 18 StrictMode.
 * Prevents double render/spawn issues and ensures proper resource cleanup.
 * 
 * @see https://github.com/scniro/react-codemirror2/issues/284#issuecomment-2133329213
 */
export const useCodeMirrorStrictModeHandlers = () => {
	const editorRef = useRef<CodeMirror.Editor | null>(null);
	const wrapperRef = useRef<ReactCodeMirror | null>(null);

	const handleEditorDidMount = useCallback((editorElement: CodeMirror.Editor) => {
		editorRef.current = editorElement;
	}, []);

	const handleEditorWillUnmount = useCallback(() => {
		try {
			const editorWrapper = editorRef.current?.getWrapperElement();
			if (editorWrapper) {
				editorWrapper.remove();
			}
		} catch (error) {
			console.warn('Error removing CodeMirror wrapper:', error);
		}

		editorRef.current = null;

		try {
			if (wrapperRef.current) {
				(wrapperRef.current as unknown as CodeMirrorWrapper).hydrated = false;
			}
		} catch (error) {
			console.warn('Error marking wrapper as non-hydrated:', error);
		}
	}, []);

	return {
		editorRef,
		wrapperRef,
		handleEditorDidMount,
		handleEditorWillUnmount
	};
};
